/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useEffect, useRef, useCallback } from 'react';
import { Platform } from 'react-native';

const TRANSPARENT_BACKGROUNDS = ['rgba(0, 0, 0, 0)', 'transparent'];
const BACKDROP_VIEWPORT_COVERAGE = 0.9;
const BACKDROP_TARGET_OPACITY = 1;
const BACKDROP_STALLED_FRAMES = 3;
const BACKDROP_WATCH_TIMEOUT_MS = 1500;

const findWebBackdrops = (): HTMLElement[] => {
  if (Platform.OS !== 'web' || typeof document === 'undefined') {
    return [];
  }

  const { innerWidth, innerHeight } = window;

  return Array.from(document.querySelectorAll<HTMLElement>('div')).filter(
    (element) => {
      const style = window.getComputedStyle(element);

      if (style.position !== 'absolute' && style.position !== 'fixed') {
        return false;
      }
      if (TRANSPARENT_BACKGROUNDS.includes(style.backgroundColor)) {
        return false;
      }

      const rect = element.getBoundingClientRect();
      return (
        rect.width >= innerWidth * BACKDROP_VIEWPORT_COVERAGE &&
        rect.height >= innerHeight * BACKDROP_VIEWPORT_COVERAGE
      );
    },
  );
};

export const useBottomSheetBackdropWebFix = (isOpen: boolean) => {
  const backdropWatchFrame = useRef<number | null>(null);

  const stopBackdropWatch = useCallback(() => {
    if (backdropWatchFrame.current !== null) {
      cancelAnimationFrame(backdropWatchFrame.current);
      backdropWatchFrame.current = null;
    }
  }, []);

  const startBackdropWatch = useCallback(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }

    stopBackdropWatch();

    const startedAt = Date.now();
    let previousOpacity: string | null = null;
    let stalledFrames = 0;

    const inspectBackdrops = () => {
      const backdrops = findWebBackdrops();
      const timedOut = Date.now() - startedAt > BACKDROP_WATCH_TIMEOUT_MS;

      if (backdrops.length > 0) {
        const currentOpacity = window.getComputedStyle(backdrops[0]).opacity;

        if (Number(currentOpacity) >= BACKDROP_TARGET_OPACITY) {
          backdropWatchFrame.current = null;
          return;
        }

        stalledFrames =
          currentOpacity === previousOpacity ? stalledFrames + 1 : 0;
        previousOpacity = currentOpacity;

        if (stalledFrames >= BACKDROP_STALLED_FRAMES || timedOut) {
          backdrops.forEach((backdrop) =>
            backdrop.style.setProperty(
              'opacity',
              String(BACKDROP_TARGET_OPACITY),
            ),
          );
          backdropWatchFrame.current = null;
          return;
        }
      } else if (timedOut) {
        backdropWatchFrame.current = null;
        return;
      }

      backdropWatchFrame.current = requestAnimationFrame(inspectBackdrops);
    };

    backdropWatchFrame.current = requestAnimationFrame(inspectBackdrops);
  }, [stopBackdropWatch]);

  useEffect(() => {
    if (isOpen) {
      startBackdropWatch();
    } else {
      stopBackdropWatch();
    }
    return stopBackdropWatch;
  }, [isOpen, startBackdropWatch, stopBackdropWatch]);
};
